import React, { useState, useEffect } from 'react';
import { XIcon } from 'lucide-react';
import type { Service, ServiceFormData } from '../../types/services';

interface ServiceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ServiceFormData) => Promise<void>;
  editingService: Service | null;
  isSaving: boolean;
}

function getInitialFormState(): ServiceFormData {
  return {
    name: '',
    description: '',
    default_price: undefined,
    is_active: true,
    sort_order: 0,
  };
}

export function ServiceFormModal({
  isOpen,
  onClose,
  onSave,
  editingService,
  isSaving,
}: ServiceFormModalProps) {
  const [formData, setFormData] = useState<ServiceFormData>(getInitialFormState());
  const [formError, setFormError] = useState<string | null>(null);

  const isEditMode = Boolean(editingService);

  useEffect(() => {
    if (isOpen && editingService) {
      setFormData({
        name: editingService.name,
        description: editingService.description || '',
        default_price: editingService.default_price ?? undefined,
        is_active: editingService.is_active,
        sort_order: editingService.sort_order,
      });
    } else if (isOpen && !editingService) {
      setFormData(getInitialFormState());
    }
    setFormError(null);
  }, [isOpen, editingService]);

  const handleSubmit = async () => {
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('Service name is required');
      return;
    }

    try {
      await onSave({
        ...formData,
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
      });
      handleClose();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to save service');
    }
  };

  const handleClose = () => {
    setFormData(getInitialFormState());
    setFormError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-[#212121]">
            {isEditMode ? 'Edit Service' : 'Add Service'}
          </h2>
          <button onClick={handleClose} className="text-gray-500">
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {formError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Service Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              placeholder="e.g., Initial Consultation"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Default Price (₹)
            </label>
            <input
              type="number"
              value={formData.default_price ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  default_price: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              placeholder="0"
              min="0"
              step="1"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be pre-filled when adding this service to a payment
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none"
              placeholder="Brief description of the service"
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-[#212121]">Active</label>
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, is_active: !prev.is_active }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.is_active ? 'bg-[#5B8E87]' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3 sticky bottom-0">
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className={`flex-1 font-semibold py-3 rounded-xl transition-colors ${
              isSaving
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]'
            }`}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Service' : 'Add Service'}
          </button>
        </div>
      </div>
    </div>
  );
}
